import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { Badge } from "@/components/ui/badge";
import { Share2, X, Users, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ShareDialogProps {
  resourceType: "brand" | "persona" | "product" | "campaign" | "narrative_generation";
  resourceId: string;
  resourceName: string;
  workspaceId: string;
  triggerClassName?: string;
  iconOnly?: boolean;
}

interface WorkspaceUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
}

interface CurrentUser {
  id: string;
  isMaster?: boolean;
  groupId?: string | null;
}

interface ShareRecord {
  id: string;
  resourceType: string;
  resourceId: string;
  sharedByUserId: string;
  sharedWithUserId: string;
  permission: string;
  createdAt: string;
  sharedWithUser: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
  sharedByUser: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
}

const RESOURCE_LABELS: Record<string, string> = {
  brand: "Brand",
  persona: "Persona",
  product: "Product",
  campaign: "Campaign",
  narrative_generation: "Narrative",
};

export function ShareDialog({ resourceType, resourceId, resourceName, workspaceId, triggerClassName, iconOnly }: ShareDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [permission, setPermission] = useState("view");
  const { toast } = useToast();

  const { data: workspaceUsers = [] } = useQuery<WorkspaceUser[]>({
    queryKey: ["/api/workspace-users", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/workspace-users?workspaceId=${workspaceId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
    enabled: open && !!workspaceId,
  });

  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ["/api/auth/user"],
    enabled: open,
  });

  const { data: shares = [], isLoading: sharesLoading } = useQuery<ShareRecord[]>({
    queryKey: ["/api/shares", resourceType, resourceId],
    queryFn: async () => {
      const res = await fetch(`/api/shares?resourceType=${resourceType}&resourceId=${resourceId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch shares");
      return res.json();
    },
    enabled: open,
  });

  const shareMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/shares", {
        resourceType,
        resourceId,
        sharedWithUserId: selectedUserId,
        permission,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shares", resourceType, resourceId] });
      setSelectedUserId("");
      toast({ title: "Shared successfully" });
    },
    onError: (err: any) => {
      toast({ title: "Error sharing", description: err.message, variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (shareId: string) => {
      return apiRequest("DELETE", `/api/shares/${shareId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shares", resourceType, resourceId] });
      toast({ title: "Share removed" });
    },
  });

  const sharedUserIds = new Set(shares.map(s => s.sharedWithUserId));
  const availableUsers = workspaceUsers.filter(u => !sharedUserIds.has(u.id));
  const noGroupAssigned = !currentUser?.isMaster && !currentUser?.groupId;

  function getUserDisplayName(user: { firstName: string | null; lastName: string | null; email: string | null }) {
    if (user.firstName || user.lastName) {
      return [user.firstName, user.lastName].filter(Boolean).join(" ");
    }
    return user.email || "Unknown";
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {iconOnly ? (
          <Button
            variant="ghost"
            size="icon"
            className={triggerClassName}
            data-testid={`share-btn-${resourceType}-${resourceId}`}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={triggerClassName}
            data-testid={`share-btn-${resourceType}-${resourceId}`}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Share {RESOURCE_LABELS[resourceType]}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">
            {resourceName}
            {resourceType === "brand" && (
              <span className="block text-xs mt-1">Sharing a brand gives access to all its personas, products, campaigns, and narratives.</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex gap-2">
            <SearchableSelect
              value={selectedUserId}
              onValueChange={setSelectedUserId}
              placeholder="Select a user..."
              testId="share-user-select"
              triggerClassName="flex-1"
              options={availableUsers.map(u => ({
                value: u.id,
                label: `${getUserDisplayName(u)} ${u.email ?? ""}`,
                testId: `share-user-option-${u.id}`,
              }))}
              emptyContent={noGroupAssigned ? (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center" data-testid="share-empty-no-group">
                  Ask an admin to assign you to a group to share with teammates.
                </div>
              ) : (
                <div className="px-2 py-3 text-sm text-muted-foreground text-center" data-testid="share-empty-no-users">
                  No more users to share with
                </div>
              )}
            />

            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger className="w-24" data-testid="share-permission-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={() => shareMutation.mutate()}
              disabled={!selectedUserId || shareMutation.isPending}
              data-testid="share-submit-btn"
            >
              {shareMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Share"}
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Shared with</h4>
            {sharesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Not shared with anyone yet.</p>
            ) : (
              <div className="space-y-2">
                {shares.map(share => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50"
                    data-testid={`share-entry-${share.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                        {share.sharedWithUser?.firstName?.[0] || share.sharedWithUser?.email?.[0] || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {share.sharedWithUser ? getUserDisplayName(share.sharedWithUser) : "Unknown"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {share.sharedWithUser?.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {share.permission}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeMutation.mutate(share.id)}
                        disabled={removeMutation.isPending}
                        data-testid={`share-remove-${share.id}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
